import type { Chat } from '@trinity/chat';
import { ChatCitationType, EngagementType, InteractionTypeEnum } from '@trinity/chat';
import type { FilterType, FilterEntry } from './interfaces.types';

export const FILTER_TYPES: FilterType[] = [
  'contains',
  'does-not-contain',
  'equals',
  'does-not-equal',
  'starts-with',
  'ends-with',
  'is-empty',
  'is-not-empty',
  'is within',
];

export function isFilterValid<T extends string>(filter: FilterEntry<T>) {
  if (!filter.key || !filter.type) return false;

  if (!doesFilterTypeRequireValue(filter.type)) return true;

  if (filter.type === 'is within') {
    return isDistanceFilterValid(filter as FilterEntry<'is within'>);
  }

  return !!filter.value?.trim();
}

function isDistanceFilterValid(filter: FilterEntry<'is within'>) {
  const value = Number(filter.value);
  return !isNaN(value) && value > 0;
}

export function doesFilterTypeRequireValue(type: FilterType) {
  const types: FilterType[] = ['is-empty', 'is-not-empty'];
  return !types.includes(type);
}

export function getEngagementType(citation: Chat.Citation): EngagementType[] {
  const proactiveSources = [
    ChatCitationType.ScientificLeaderTopic,
    ChatCitationType.ClinicalLeaderTopic,
    ChatCitationType.DigitalLeaderTopic,
    ChatCitationType.Association,
    ChatCitationType.Affiliation,
    ChatCitationType.Collaboration,
    ChatCitationType.CompanySponsorship,
    ChatCitationType.ClinicalTrial,
    ChatCitationType.Conference,
    ChatCitationType.Publication,
    ChatCitationType.CongressAbstract,
    ChatCitationType.ContactDetail,
    ChatCitationType.UnmetNeed,
  ];

  const reactiveSources = [ChatCitationType.DocumentedInterests];

  if (citation.type === ChatCitationType.CallCenterInteractions) {
    return deriveEngagementFromSnippets(citation.snippets, isReactiveCallCenterInteraction);
  }
  if (citation.type === ChatCitationType.FbmsInteractions) {
    return deriveEngagementFromSnippets(citation.snippets, isReactiveMslInteraction);
  }
  if (citation.type == ChatCitationType.CrmNote) {
    return deriveCrmNoteEngagement(citation.metadata as Chat.CrmNoteCitationMetadata);
  }
  if (proactiveSources.includes(citation.type)) {
    return [EngagementType.Proactive];
  }
  if (reactiveSources.includes(citation.type)) {
    return [EngagementType.Reactive];
  }
  return [EngagementType.None];
}

function deriveEngagementFromSnippets(
  snippets: { metadata: unknown }[],
  isReactiveFn: (metadata: never) => boolean
): EngagementType[] {
  const reactive = snippets.some((snippet) => isReactiveFn(snippet.metadata as never));
  const proactive = snippets.some((snippet) => !isReactiveFn(snippet.metadata as never));
  return [...(reactive ? [EngagementType.Reactive] : []), ...(proactive ? [EngagementType.Proactive] : [])];
}

function deriveCrmNoteEngagement(metadata: Chat.CrmNoteCitationMetadata): EngagementType[] {
  if (metadata.type === InteractionTypeEnum.DocumentedInterest) return [EngagementType.Reactive];
  if (metadata.type === InteractionTypeEnum.CallCenterInteraction) {
    return isReactiveCallCenterInteraction(metadata) ? [EngagementType.Reactive] : [EngagementType.Proactive];
  }
  if (metadata.type === InteractionTypeEnum.FbmsInteraction) {
    return isReactiveMslInteraction(metadata) ? [EngagementType.Reactive] : [EngagementType.Proactive];
  }
  return [EngagementType.None];
}

export function isReactiveCallCenterInteraction(metadata: Chat.CallCenterInteractionMetadata) {
  return ['mir', 'medical information request'].includes(metadata?.interactionSubType?.toLowerCase());
}
export function isReactiveMslInteraction(metadata: Chat.FBMSInteractionMetadata) {
  return ['reactive'].includes(metadata?.interactionSubType?.toLowerCase());
}

export function getEngagementTypeFromCitationType(
  citationType: ChatCitationType,
  mir: boolean
): EngagementType {
  const proactiveSources = [
    ChatCitationType.ScientificLeaderTopic,
    ChatCitationType.ClinicalLeaderTopic,
    ChatCitationType.DigitalLeaderTopic,
    ChatCitationType.Association,
    ChatCitationType.Affiliation,
    ChatCitationType.Collaboration,
    ChatCitationType.CompanySponsorship,
    ChatCitationType.ClinicalTrial,
    ChatCitationType.Conference,
    ChatCitationType.Publication,
    ChatCitationType.CongressAbstract,
    ChatCitationType.ContactDetail,
    ChatCitationType.UnmetNeed,
    ChatCitationType.FbmsInteractions,
  ];

  const reactiveSources = [ChatCitationType.DocumentedInterests];

  if (citationType === ChatCitationType.CallCenterInteractions) {
    if (mir) {
      return EngagementType.Reactive;
    } else return EngagementType.Proactive;
  }
  if (proactiveSources.includes(citationType)) {
    return EngagementType.Proactive;
  }
  if (reactiveSources.includes(citationType)) {
    return EngagementType.Reactive;
  }
  return EngagementType.None;
}
